import * as React from 'react'
import styled from 'styled-components'
import tiny from 'tinycolor2'
import {useTranslation} from 'react-i18next'
import {sortByName} from '../../../../config/utils'

import {Text} from '../../atoms/Text'
import {colors} from '../../../colors'

export interface SuspendedPeriod {
  id: string
  siteId: string
  actor: string
  createdAt: Date
  startAt: Date
  endAt: Date
  alarmsEnabled: boolean
  reason: string
}

export interface Site {
  id: string
  address?: string
  city?: string
  contactEmail?: string
  contactName?: string
  contactPhone?: string
  internalId?: string
  country?: string
  description?: string
  postalCode?: string
  state?: string
  locale: string //  "fi-FI"
  name: string //  "Kamppi"
  temperatureUnit: string //  "C"
  timeZone: string //  "Europe/Helsinki"
  weightUnit: string //  "G"
  suspendedPeriods: SuspendedPeriod[]
  aromiIntegration?: boolean
  aromiImportedDataRetentionDays?: number
}

export interface Location {
  id: string
  name: string
  sites: Site[]
  visible: boolean
}

export interface LocationGroup {
  id: string
  name: string
  locations: Location[]
  visible: boolean
}

export interface Chain {
  id: string
  name: string
  description?: string
  brands: Brand[]
}

export interface Brand {
  id: string
  name: string
  locationGroups: LocationGroup[]
  visible: boolean
}

export interface SiteSelectorProps {
  locationGroups: LocationGroup[]
  chain: Chain
  selectedSiteName: string
  chainSelectText: string
  onClick: (a: {id: string; name: string; type: OrgLevelType}) => void
}

export enum OrgLevelType {
  ORG = 'org',
  CHAIN = 'chain',
  BRAND = 'brand',
  LOCATION_GROUP = 'group',
  LOCATION = 'location',
  SITE = 'site'
}

const SiteText = styled.div`
  margin: 0 3rem 1rem 3rem;
  text-align: left;
`

const SiteSelectorModal = styled.div`
  min-width: 336px;
`

const Divider = styled.hr`
  margin: 1rem 0 0.5rem 0;
  border: transparent;
  border-bottom: 1px solid
    ${tiny(colors.system.lightGrey_5)
      .darken(15)
      .toHexString()};
`

const LocationGroupSection = styled.div`
  margin: 1rem 1.5rem;
`

const SearchInput = styled.input`
  width: 100%;
  margin: 0rem;
  padding: 0.5rem;
  border: 1px solid
    ${tiny(colors.system.lightGrey_5)
      .darken(15)
      .toHexString()};
  border-radius: 4px;
  font-size: 14px;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: ${colors.system.blue};
    box-shadow: 0 0 0 2px ${colors.system.blue}33;
  }
`

const HighlightedText = styled.span`
  background-color: #ffeb3b;
  font-weight: 600;
`

// Helper function to highlight matching text
const highlightText = (text: string, searchTerm: string): React.ReactNode => {
  if (!searchTerm.trim()) return text

  const cleanSearchTerm = searchTerm.trim()

  // Escape special regex characters in the search term
  const escapedTerm = cleanSearchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const regex = new RegExp(`(${escapedTerm})`, 'gi')
  const parts = text.split(regex)

  return parts.map((part, index) => {
    // Check if part matches the search term (case-insensitive)
    if (part && part.toLowerCase() === cleanSearchTerm.toLowerCase()) {
      return <HighlightedText key={index}>{part}</HighlightedText>
    }
    return part
  })
}

export const SiteSelector = (props: SiteSelectorProps) => {
  const {chain, locationGroups, onClick, chainSelectText} = props
  const [searchTerm, setSearchTerm] = React.useState('')
  const {t} = useTranslation('locations')

  // Filter location groups and sites based on search term and sort alphabetically
  const filteredLocationGroups = React.useMemo(() => {
    const searchLower = searchTerm.toLowerCase().trim()

    if (!searchLower) {
      return locationGroups
        .map(locationGroup => ({
          ...locationGroup,
          locations: locationGroup.locations
            .slice()
            .map(location => ({
              ...location,
              sites: location.sites.slice().sort(sortByName)
            }))
            .sort(sortByName)
        }))
        .slice()
        .sort(sortByName)
    }

    return locationGroups
      .map(locationGroup => ({
        ...locationGroup,
        locations: locationGroup.locations
          .map(location => ({
            ...location,
            sites: location.sites
              .filter(site => {
                return (
                  site.name.toLowerCase().includes(searchLower) ||
                  location.name.toLowerCase().includes(searchLower) ||
                  locationGroup.name.toLowerCase().includes(searchLower)
                )
              })
              .slice()
              .sort(sortByName)
          }))
          .filter(location => location.sites.length > 0)
          .slice()
          .sort(sortByName)
      }))
      .filter(locationGroup => locationGroup.locations.length > 0)
      .slice()
      .sort(sortByName)
  }, [searchTerm, locationGroups])

  return (
    <SiteSelectorModal data-cy="site-selector-modal">
      <LocationGroupSection>
        <Text size="S" onClick={() => onClick({id: chain.id, name: chain.name, type: OrgLevelType.CHAIN})}>
          {chainSelectText}
        </Text>
      </LocationGroupSection>
      <Divider />
      <LocationGroupSection>
        <SearchInput
          type="text"
          placeholder={t('locations:placeholders.searchLocationOrSite', 'Search location or site')}
          value={searchTerm}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
          onMouseDown={(e: React.MouseEvent<HTMLInputElement>) => e.stopPropagation()}
          onClick={(e: React.MouseEvent<HTMLInputElement>) => e.stopPropagation()}
          data-cy="site-filter-input"
        />
      </LocationGroupSection>
      <Divider />
      {searchTerm.trim() && filteredLocationGroups.length === 0 ? (
        <LocationGroupSection>
          <Text size="S">
            {t('locations:messages.noResultsFound', 'No results found for')} "{searchTerm}"
          </Text>
        </LocationGroupSection>
      ) : (
        filteredLocationGroups.map((locationGroup, key) => (
          <div key={locationGroup.id}>
            <LocationGroupSection>
              {locationGroup.visible && (
                <Text
                  size="S"
                  onClick={() =>
                    onClick({
                      id: locationGroup.id,
                      name: locationGroup.name,
                      type: OrgLevelType.LOCATION_GROUP
                    })
                  }
                >
                  {highlightText(locationGroup.name, searchTerm)}
                </Text>
              )}
            </LocationGroupSection>
            {locationGroup.locations.map(location => (
              <div key={location.id}>
                {location.sites.map(site => (
                  <SiteText key={site.id}>
                    <Text
                      size="S"
                      onClick={() => onClick({id: site.id, name: site.name, type: OrgLevelType.SITE})}
                      key={site.id}
                    >
                      {location.visible ? (
                        <>
                          {highlightText(location.name, searchTerm)}
                          {' - '}
                          {highlightText(site.name, searchTerm)}
                        </>
                      ) : (
                        highlightText(site.name, searchTerm)
                      )}
                    </Text>
                  </SiteText>
                ))}
              </div>
            ))}
            {key !== filteredLocationGroups.length - 1 && <Divider />}
          </div>
        ))
      )}
    </SiteSelectorModal>
  )
}
